import kebabCase from "lodash.kebabcase";
import replace from "lodash.replace";
import { ALLOWED_IMAGE_TYPES, BADGE_DATE_TIME_FORMAT, QR_VISITOR_TYPES } from "common/constants";
import hashUtils from "common/utils/hashUtils";
import dayjs from "common/dayjs";
import { IPersonBadge, IProduct } from "common/types";
import { IQRCodeEntity } from "./types";
import { v4 as uuid } from "uuid";
import { getDateTicks } from "../../../../../common/utils/dateUtils";

export const getPDFName = (displayName: string) =>
  `${kebabCase(replace(displayName, /[^\p{L} ]/gu, "").trim())}_badge_${dayjs(new Date()).format(BADGE_DATE_TIME_FORMAT)}.pdf`;

export const findAllowedTypeInContentType = (contentType: string) => ALLOWED_IMAGE_TYPES.find(type => contentType.includes(type));

export const getIdInBrackets = (companyName: string, standId: string) =>
  40 - companyName.length - 3 >= standId.length ? " (" + standId + ")" : "";

export const removeMultipleSpacesFromString = (val: string | undefined) => {
  if (!val) {
    return "";
  }
  return val.replace(/ +(?= )/g, "");
};

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const DEFAULT_TOTAL_ADDONS = 30;
const MAX_SUPPORTED_ADDON_INDEX = 30;

const getEntitlementInfo = (rawEntitlementCode: string = "") => {
  const [ebsProductCode, addonCode] = rawEntitlementCode.split(",");
  return { ebsProductCode, addonCode };
};

const collectAddonIndexesBySku = (products: IProduct[] = []) => {
  return products.reduce<Record<string, number>>((acc, product) => {
    product.items?.forEach(item => {
      if (item.product?.sku && typeof item.product?.rx_addon_index === "number") {
        acc[item.product.sku] = item.product.rx_addon_index;
      }
      item.options?.forEach(option => {
        if (option.product?.sku && typeof option.product?.rx_addon_index === "number") {
          acc[option.product.sku] = option.product.rx_addon_index;
        }
      });
    });
    return acc;
  }, {});
};

const getTotalAddons = (addonIndexesBySku: Record<string, number>) => {
  const indexes = Object.values(addonIndexesBySku).filter(index => index >= 0 && index <= MAX_SUPPORTED_ADDON_INDEX);
  if (!indexes.length) {
    return DEFAULT_TOTAL_ADDONS;
  }
  return Math.max(...indexes) + 1;
};

const encodeAddons = (indices: number[], totalAddons = DEFAULT_TOTAL_ADDONS) => {
  let mask = 0;
  for (const i of indices) {
    if (i >= 0 && i <= MAX_SUPPORTED_ADDON_INDEX) {
      mask |= 1 << i;
    }
  }

  if (!mask) {
    return "";
  }

  let encoded = "";
  let temp = mask;
  while (temp > 0) {
    const rem = temp % 64;
    temp = Math.floor(temp / 64);
    encoded = B64[rem] + encoded;
  }

  const length = Math.ceil(totalAddons / 6);
  return encoded.padStart(length, "A");
};

const getEncodedBadgeAddons = (updatedBadge: IPersonBadge, products: IProduct[] = []) => {
  const addonCodes = updatedBadge.addonProductCodes;
  if (!addonCodes || addonCodes.length === 0) {
    return "";
  }

  const addonIndexesBySku = collectAddonIndexesBySku(products);
  const indexes = [...new Set(addonCodes.map(code => addonIndexesBySku[code]).filter(index => Number.isInteger(index)))].sort(
    (a, b) => a - b
  ) as number[];
  if (!indexes.length) {
    return "";
  }
  return encodeAddons(indexes, getTotalAddons(addonIndexesBySku));
};

const getEntitlementWithAddons = (
  entitlementId: string,
  updatedBadge: IPersonBadge,
  products: IProduct[] = [],
  currentAddonCode?: string
) => {
  const encodedAddons = getEncodedBadgeAddons(updatedBadge, products);
  if (encodedAddons) {
    return `${entitlementId},${encodedAddons}`;
  }
  const addonSelection = updatedBadge.addonProductCodes;
  const hasExplicitAddonSelection = Array.isArray(addonSelection);
  if (currentAddonCode && (!hasExplicitAddonSelection || addonSelection.length > 0)) {
    return `${entitlementId},${currentAddonCode}`;
  }
  return entitlementId;
};

const generateQRCodeContent = (gbsCode: string, badgeId: string, firstName: string, version: string, ebsCode: string, secret: string) => {
  if (!secret) {
    return "";
  }
  const contentForHash = `${badgeId}|${version}|${ebsCode}`;
  const content = `${badgeId}|${firstName}|${version}|${ebsCode}`;
  const hash = hashUtils.computeHash(contentForHash, secret, true);
  return `${gbsCode}|${content}|${hash}`;
};

const parseQRCodeString = (qrString: string | undefined, customerType: string): IQRCodeEntity | undefined => {
  if (!qrString) {
    return undefined;
  }
  const valuesArray = qrString.split("|");
  const version = Number(valuesArray && valuesArray[3] && valuesArray[3]!.substring(valuesArray[3]!.indexOf("-") + 1));
  const { ebsProductCode, addonCode } = getEntitlementInfo(valuesArray[4]);
  return {
    gbsCode: valuesArray[0]!,
    badgeId: valuesArray[1]!,
    firstName: valuesArray[2]!,
    badgeVersion: isNaN(version) ? 1 : version,
    badgeVersionCode: QR_VISITOR_TYPES[customerType],
    ebs_product_code: ebsProductCode,
    addonCode,
    hash: valuesArray[5]!
  };
};

const getUpdatedQrContent = (
  currentContent: string | undefined,
  updatedBadge: IPersonBadge,
  entitlementId: string,
  secret: string,
  skipVersion = false,
  products: IProduct[] = []
) => {
  const { contact } = updatedBadge;
  try {
    const parsed = parseQRCodeString(currentContent, updatedBadge.customerType)!;
    const { gbsCode, badgeId, badgeVersion, badgeVersionCode, addonCode } = parsed;
    const updateOffset = skipVersion ? 0 : 1;
    const updatedCode = `${badgeVersionCode}-${updatedBadge?.badgeVersion || badgeVersion + updateOffset}`;
    const entitlementWithAddons = getEntitlementWithAddons(entitlementId, updatedBadge, products, addonCode);
    return generateQRCodeContent(gbsCode, badgeId, contact.firstName, updatedCode, entitlementWithAddons, secret);
  } catch (e) {
    return "";
  }
};

const generateBadgeId = () => {
  const maxElapsedTicksLength = 16;
  const beginDate = new Date(2021, 1, 1);
  const currentDate = dayjs(new Date()).utc().toDate();
  const diff = (getDateTicks(currentDate) - getDateTicks(beginDate)).toString();
  return `${diff.padStart(maxElapsedTicksLength, "0").slice(0, maxElapsedTicksLength)}-${uuid().slice(0, 3).toUpperCase()}`;
};

const generateQRContentForNewReg = (
  gbsCode: string,
  firstName: string,
  ebsCode: string,
  secret: string,
  customerType: string,
  id: string
) => {
  const version = `${QR_VISITOR_TYPES[customerType]}-1`;
  return generateQRCodeContent(gbsCode, id, firstName, version, ebsCode, secret);
};

const QRUtils = {
  generateQRCodeContent,
  getUpdatedQrContent,
  parseQRCodeString,
  generateBadgeId,
  generateQRContentForNewReg
};

export default QRUtils;
